import { createFileRoute } from '@tanstack/react-router'
import { FabricExtractorsPage, type FabricExtractorsParams } from '@bloo-team/product-fabric'

/**
 * Composer shim (S5.0, docs/modularization.md §Route mounting). `?field=<STREAM>/<Field>`
 * narrows the catalogue to the extractors that write that data-model field
 * (the Data Model's field rows pivot here — docs/data-model-requirements.md
 * §Pivots); the page shows it as a removable chip. `?filter=<chip>` seeds the
 * chip row (Fabric Overview's checks land here pre-filtered).
 */
function ExtractorsRoute() {
  const { clusterId, scopeId } = Route.useParams()
  const { field, filter } = Route.useSearch()
  return <FabricExtractorsPage clusterId={clusterId} scopeId={scopeId} field={field} filter={filter} />
}

type ListFilter = NonNullable<FabricExtractorsParams['filter']>

/**
 * `?filter=<chip>` (S1.4) — the page's chip keys except `all`, which is the
 * absence of the param. `satisfies` keeps this in lockstep with `ListFilter`:
 * a chip added or renamed there fails typecheck here.
 */
const FILTER_KEYS = {
  attention: true, silent: true, fallout: true, enabled: true,
  disabled: true, custom: true, draft: true, local: true,
} as const satisfies Record<Exclude<ListFilter, 'all'>, true>

const isFilterKey = (v: unknown): v is keyof typeof FILTER_KEYS =>
  typeof v === 'string' && Object.prototype.hasOwnProperty.call(FILTER_KEYS, v)

export const Route = createFileRoute('/$clusterId/$scopeId/fabric/extractors')({
  validateSearch: (search: Record<string, unknown>): FabricExtractorsParams => ({
    field: typeof search.field === 'string' && search.field.trim() ? search.field.trim() : undefined,
    filter: isFilterKey(search.filter) ? search.filter : undefined,
  }),
  component: ExtractorsRoute,
})
